import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip } from "@/components/common/StatusChip";
import { Amount } from "@/components/common/Amount";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { prioridadTone } from "@/lib/wo";
import { cn } from "@/lib/utils";
import { ProposalPanel } from "@/features/schedule/ProposalPanel";

const INTERNAL_ROLES = ["owner", "socio_operativo", "operaciones", "ventas", "supervisor"];

const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const nextBusinessDay = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Fix T (2026-10-05): en «Por programar» la tarjeta aclara si ya tiene contratista (asignada,
// falta el día) o si no tiene ninguno, y si tenía un día que ya pasó.
const PendienteInfo = ({ wo, fecha }) => {
  const { t } = useT();
  return (
    <p className="mt-1 truncate text-[11px] text-ink-tertiary" data-testid={`schedule-pending-info-${wo.id}`}>
      {wo.sub_id ? t("schedule.assigned_no_day", { sub: wo.sub_nombre || "—" }) : t("schedule.no_contractor")}
      {wo.fecha_programada && wo.fecha_programada < fecha
        ? ` · ${t("schedule.was_scheduled", { fecha: wo.fecha_programada.split("-").reverse().join("/") })}` : ""}
    </p>
  );
};

const Card = ({ wo, draggable, onDragStart, pendiente, fecha }) => (
  <Link
    to={`/ordenes/${wo.id}`}
    draggable={draggable}
    onDragStart={(e) => { e.dataTransfer.setData("text/plain", wo.id); onDragStart?.(wo); }}
    data-testid={`schedule-card-${wo.id}`}
    className="block cursor-grab border border-line bg-paper px-2.5 py-2 transition-colors duration-[120ms] hover:border-line-strong active:cursor-grabbing"
  >
    <div className="flex items-start justify-between gap-1.5">
      <p className="min-w-0 truncate font-mono text-[11.5px] text-ink-tertiary">{wo.code}</p>
      <StatusChip tone={prioridadTone(wo.prioridad)} label="" className="shrink-0 px-1" />
    </div>
    <p className="mt-0.5 truncate text-[12.5px] font-medium text-ink">{wo.property_nombre}</p>
    <p className="truncate text-[11.5px] text-ink-secondary">
      {wo.unit_etiqueta ? `${wo.unit_etiqueta} · ` : ""}{wo.service_nombre}
    </p>
    {wo.client_price_cents !== undefined && !!wo.client_price_cents && (
      <Amount cents={wo.client_price_cents} className="mt-1 text-[11.5px]" />
    )}
    {pendiente && <PendienteInfo wo={wo} fecha={fecha} />}
  </Link>
);

export default function SchedulePage() {
  const { t } = useT();
  const { user } = useAuth();
  const { brandParam } = useBrand();
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(todayLocal());
  const [proposalOpen, setProposalOpen] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["schedule-dia", fecha, brandParam],
    queryFn: async () => (await api.get("/work-orders/schedule/dia", { params: { fecha, brand: brandParam } })).data,
    enabled: INTERNAL_ROLES.includes(user?.rol),
  });

  if (!INTERNAL_ROLES.includes(user?.rol)) return <Navigate to="/" replace />;

  const refresh = () => qc.invalidateQueries({ queryKey: ["schedule-dia"] });

  const moveTo = async (wo, sub) => {
    if (!wo) return;
    if (wo.sub_id === sub.sub_id) {
      setOverCol(null);
      return;
    }
    try {
      const cost = wo.sub_cost_cents > 0 ? wo.sub_cost_cents : (wo.tarifa_costo_sub_cents || 0);
      if (cost <= 0) {
        toast.error(t("schedule.no_cost"));
        return;
      }
      await api.post(`/work-orders/${wo.id}/assign`, {
        sub_id: sub.sub_id, sub_cost_cents: cost, asignar: true, notificar: true,
        motivo: wo.sub_id ? t("schedule.reassign_reason") : undefined,
      });
      await api.post("/work-orders/schedule/confirmar", { fecha, items: [{ work_order_id: wo.id }] });
      toast.success(t("schedule.moved"));
      refresh();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setOverCol(null);
      setDragging(null);
    }
  };

  return (
    <>
      <PageHeader
        testId="schedule-header"
        title={t("schedule.title")}
        subtitle={t("schedule.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Input
              type="date"
              data-testid="schedule-date-input"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="h-9 w-[150px] font-mono text-[13px]"
            />
            <Button
              variant="outline"
              data-testid="schedule-propose-button"
              onClick={() => setProposalOpen(true)}
              className="h-9 text-[13px]"
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {t("schedule.propose_tomorrow")}
            </Button>
          </div>
        }
      />

      <div className="flex-1 overflow-x-auto p-5" data-testid="schedule-page">
        {/* Fix T (2026-10-05): qué es «asignada» y qué es «programada» */}
        <p className="mb-3 max-w-[900px] text-[12px] text-ink-secondary" data-testid="schedule-legend">
          {t("schedule.legend")}
        </p>
        {isLoading ? (
          <div className="loading-bar" />
        ) : isError ? (
          <p data-testid="schedule-error" className="py-10 text-center text-[13px] text-state-critical">
            {t("app.error")}
          </p>
        ) : (
          <div className="flex h-full min-w-max gap-3">
            <div
              data-testid="schedule-column-sin_asignar"
              onDragOver={(e) => { e.preventDefault(); setOverCol("sin_asignar"); }}
              onDragLeave={() => setOverCol(null)}
              onDrop={(e) => { e.preventDefault(); setOverCol(null); }}
              className={cn(
                "flex w-[220px] shrink-0 flex-col border border-line bg-surface-sunken",
                overCol === "sin_asignar" && "border-line-strong"
              )}
            >
              <div className="border-b border-line px-2.5 py-2">
                <p className="text-[12.5px] font-semibold text-ink">{t("schedule.to_schedule")}</p>
                <p className="text-[11px] text-ink-tertiary">
                  {t("schedule.to_schedule_hint")} · <span className="font-mono">{(data?.sin_asignar || []).length}</span>
                </p>
              </div>
              <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
                {!data?.sin_asignar?.length ? (
                  <p className="pt-4 text-center text-[11.5px] text-ink-tertiary">{t("schedule.empty_unassigned")}</p>
                ) : (
                  data.sin_asignar.map((wo) => (
                    <Card key={wo.id} wo={wo} draggable onDragStart={setDragging} pendiente fecha={fecha} />
                  ))
                )}
              </div>
            </div>

            {(data?.columnas || []).map((col) => (
              <div
                key={col.sub_id}
                data-testid={`schedule-column-${col.sub_id}`}
                onDragOver={(e) => { e.preventDefault(); setOverCol(col.sub_id); }}
                onDragLeave={() => setOverCol(null)}
                onDrop={(e) => { e.preventDefault(); moveTo(dragging, col); }}
                className={cn(
                  "flex w-[220px] shrink-0 flex-col border border-line bg-paper",
                  overCol === col.sub_id && "border-line-strong bg-surface-sunken",
                  col.excedido && "border-state-critical"
                )}
              >
                <div className={cn("border-b border-line px-2.5 py-2", col.excedido && "bg-state-critical-soft")}>
                  <p className="truncate text-[12.5px] font-semibold text-ink">{col.sub_nombre}</p>
                  <p
                    data-testid={`schedule-occupancy-${col.sub_id}`}
                    className={cn("font-mono text-[11px]", col.excedido ? "font-semibold text-state-critical" : "text-ink-tertiary")}
                  >
                    {t("schedule.occupancy", { ocupado: col.ocupado, capacidad: col.capacidad })}
                  </p>
                </div>
                <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
                  {!col.ordenes.length ? (
                    <p className="pt-4 text-center text-[11.5px] text-ink-tertiary">{t("schedule.empty_column")}</p>
                  ) : (
                    col.ordenes.map((wo) => (
                      <Card key={wo.id} wo={wo} draggable onDragStart={setDragging} />
                    ))
                  )}
                </div>
              </div>
            ))}

            {!data?.columnas?.length && (
              <div className="flex w-[320px] shrink-0 items-center">
                <EmptyState testId="schedule-empty" title={t("schedule.empty_column")} description={t("schedule.drag_hint")} />
              </div>
            )}
          </div>
        )}
      </div>

      <ProposalPanel
        open={proposalOpen}
        onClose={() => setProposalOpen(false)}
        fecha={nextBusinessDay()}
        brand={brandParam}
        onConfirmed={() => { setFecha(nextBusinessDay()); refresh(); }}
      />
    </>
  );
}
